import { useEffect, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { withTemplate } from "@/components/TemplatePicker";
import CreditNotesSection, { SectionHeader, CreateButton } from "@/pages/_credit/SalesCreditNotes";
import DebitNotesSection from "@/pages/_debit/DebitNotesSection";
import PromesasPago from "@/pages/_sales/PromesasPago";
import { fmtL, fmtDate, fmtDateTime, apiErr, descripcionComercial } from "@/lib/format";
import { usePeriodQuery, PeriodToolbar, Pager, fmtEmision } from "@/components/PeriodQuery";
import { toast } from "sonner";
import DocumentCenter from "@/components/DocumentCenter";
import { useCompany } from "@/contexts/CompanyContext";
import { EmailHistory, SendDocEmailModal } from "@/components/DocEmail";
import { useAuth } from "@/contexts/AuthContext";
import { AprobacionInfo, RevisionModal } from "@/pages/Proformas";
import { BitacoraTarifas, TarifasModal, puedeCambiarTarifa } from "@/components/TarifasProforma";
import {
  EyeIcon, XIcon, PrinterIcon, ProhibitIcon, TruckIcon, WarningIcon, FilePdfIcon, PaperPlaneTiltIcon,
  TagIcon, CheckCircleIcon, TrashIcon, PencilSimpleIcon, HandCoinsIcon,
  PhoneIcon, UserIcon, ClockCounterClockwiseIcon, PlusIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
import { getToken } from "@/lib/token";
import { pedirPaseFactura, esRtcDescarga, conPase, descargarArchivo } from "@/components/NotaDescarga";
import BitacoraFactura from "@/components/BitacoraFactura";
import { CambioProyectoModal, useGestor } from "@/pages/_payments/CarteraCliente";

export default function Sales() {
  const navigate = useNavigate();
  const { companyId, branchId } = useCompany() || {};
  const cfgFiscalUrl = companyId && branchId
    ? `/admin/configuracion-documentos?empresa=${companyId}&sucursal=${branchId}#sec-autorizaciones`
    : "/admin/configuracion-documentos";
  // PROFORMAS | FACTURAS | NOTAS DE DÉBITO | NOTAS DE CRÉDITO (?tab= para enlaces directos)
  const [searchParams, setSearchParams] = useSearchParams();
  const tabInicial = searchParams.get("tab");
  const [tab, setTabState] = useState(TABS.some(([k]) => k === tabInicial) ? tabInicial : "proformas");
  const setTab = (t) => { setTabState(t); setSearchParams(t === "proformas" ? {} : { tab: t }, { replace: true }); };
  const [proformas, setProformas] = useState([]);
  const [vistaGuardada, setVista] = usePageState("vista-proformas", "gestion");
  const [buscarPf, setBuscarPf] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [viewingType, setViewingType] = useState(null); // "factura" | "proforma"
  const [converting, setConverting] = useState(null);
  const [caiStatus, setCaiStatus] = useState(null);
  const [convertModal, setConvertModal] = useState(null); // proforma a AUTORIZAR Y FACTURAR
  const [generada, setGenerada] = useState(null);         // factura recién emitida → opción de impresión
  // Flujo de autorización de proformas
  const { user, hasModule } = useAuth();
  const eff = user?.effective || {};
  const esRtc = Boolean(eff.alcance_regional);
  const revisa = !esRtc && Boolean(eff.is_admin || eff.revisiones?.proformas);
  const puedeFacturar = !esRtc && (!hasModule || hasModule("ventas", "crear"));
  const [revisando, setRevisando] = useState(null);
  const onEnviar = async (p) => {
    try { await api.post(`/sales/proformas/${p.id}/enviar`); toast.success(`Proforma ${p.numero} enviada para autorización`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [pf, cai] = await Promise.all([
        api.get("/sales/proformas", { params: { incluir_convertidas: true } }).catch(() => ({ data: [] })),
        api.get("/sales/cai-status").catch(() => ({ data: null })),
      ]);
      setProformas(pf.data || []);
      setCaiStatus(cai.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // AUTORIZAR Y FACTURAR: una sola acción, una sola factura (el servidor bloquea dobles envíos)
  const autorizarYFacturar = async (p, opts) => {
    if (converting) return;
    setConverting(p.id);
    try {
      const { data } = await api.post(`/sales/proformas/${p.id}/autorizar-facturar`, opts);
      toast.success(data.ya_facturada ? `La proforma ya estaba facturada: ${data.numero}` : `Factura ${data.numero} generada`);
      if (data.credito_override) toast.warning(`Crédito excedido autorizado: ${data.credito_override.razon}`);
      if (data.email_auto?.enviado) toast.success(`Factura enviada por correo a ${(data.email_auto.to || []).join(", ")}`);
      else if (data.email_auto?.intentado && data.email_auto?.error) toast.error(`No se pudo enviar el correo: ${data.email_auto.error}`);
      setConvertModal(null);
      setGenerada(data);
      load();
    } catch (e) { toast.error(apiErr(e)); load(); }
    finally { setConverting(null); }
  };
  const verEnFacturas = (fac) => {
    // La factura aparece en FACTURAS dentro del mes de su fecha de emisión
    try {
      const d = new Date(fac?.fecha || Date.now());
      const mes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      sessionStorage.setItem(`agro_view:${window.location.pathname}:periodo-facturas`,
        JSON.stringify({ modo: "mes", mes, desde: "", hasta: "" }));
    } catch { /* sin almacenamiento */ }
    setGenerada(null);
    setTab("facturas");
  };

  const onDeleteProforma = async (p) => {
    if (!window.confirm(`¿Eliminar la proforma ${p.numero}?\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/invoices/${p.id}`);
      toast.success(`Proforma ${p.numero} eliminada`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onPrintProforma = (p) => {
    // Navegación directa con token en query → funciona en iPad/Safari sin bloqueo.
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const token = getToken();
    const companyId = localStorage.getItem("agroport_company_id");
    const branchId = localStorage.getItem("agroport_branch_id");
    const params = new URLSearchParams();
    params.set("token", token || "");
    if (companyId) params.set("company_id", companyId);
    if (branchId) params.set("branch_id", branchId);
    const url = withTemplate(`${apiBase}/invoices/${p.id}/pdf?${params.toString()}`, "proforma");
    const w = window.open(url, "_blank");
    if (!w) window.location.href = url;
  };

  const view = async (doc, type) => {
    const { data } = await api.get(`/invoices/${doc.id}`);
    setViewing(data);
    setViewingType(type);
  };

  const vista = VISTAS_PROFORMA[vistaGuardada] ? vistaGuardada : "gestion";
  const pendientes = proformas.filter((p) => estadoProforma(p) === "pendiente").length;
  const enVista = proformas.filter((p) => VISTAS_PROFORMA[vista][1](estadoProforma(p)));
  const busquedaPf = useLiveSearch(enVista, (p) => [p.numero, p.cliente_nombre, p.cliente_codigo, p.cliente_rtn, p.vendedor_nombre],
    { estado: [buscarPf, setBuscarPf] });
  const proformasVista = busquedaPf.resultados;
  const tabs = esRtc ? TABS.slice(0, 3) : TABS;

  return (
    <Layout
      title="VENTAS Y FACTURACIÓN"
      subtitle="PROFORMAS · FACTURAS · PROMESAS DE PAGO · NOTAS DE DÉBITO · NOTAS DE CRÉDITO — Facturación SAR (CAI · ISV 15/18%)"
    >
      {/* Alerta CAI — CAI activo por tipo de documento (factura, NC, ND) */}
      {caiStatus?.alerta && (
        <div className={`mb-4 px-4 py-3 rounded-md border flex items-start gap-2 text-sm ${
          caiStatus.estado === "vencido" || caiStatus.estado === "no_configurado" ? "bg-red-500/10 border-red-500/40 text-red-300" :
          caiStatus.estado === "por_vencer" ? "bg-orange-500/10 border-orange-500/40 text-orange-300" :
          "bg-agro-yellow/10 border-agro-yellow/40 text-agro-yellow"
        }`} data-testid="cai-alert">
          <WarningIcon size={18} weight="fill" className="mt-0.5 shrink-0" />
          <div className="flex-1 space-y-0.5">
            {(caiStatus.alertas && caiStatus.alertas.length ? caiStatus.alertas : [caiStatus]).map((a, i) => (
              <div key={a.tipo_documento || i}>
                <strong>CAI</strong> · {a.mensaje}
                {a.estado !== "no_configurado" && a.restantes != null && (
                  <span className="ml-2 text-xs opacity-80">({a.restantes} correlativos restantes — {a.pct_usado}% usado · {a.dias_restantes} días)</span>
                )}
              </div>
            ))}
          </div>
          <button onClick={() => navigate(cfgFiscalUrl)} data-testid="cai-alert-update-btn"
            title="Configuración de documentos y datos fiscales"
            className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md border border-current hover:bg-white/10">
            GESTIONAR AUTORIZACIONES
          </button>
        </div>
      )}

      {/* PROFORMAS | FACTURAS | NOTAS DE DÉBITO | NOTAS DE CRÉDITO */}
      <div className="flex gap-1 mb-4 border-b border-border/60 flex-wrap" data-testid="sales-tabs">
        {tabs.map(([k, l]) => (
          <TabBtn key={k} active={tab === k} onClick={() => setTab(k)} testid={`tab-${k}`}>
            {l}
            {k === "proformas" && (
              <span className={`ml-1.5 text-[10px] px-1.5 rounded ${pendientes ? "bg-agro-yellow/15 text-agro-yellow" : "opacity-70"}`}
                title="PROFORMAS PENDIENTES DE AUTORIZACIÓN" data-testid="proformas-pendientes-count">{pendientes}</span>
            )}
          </TabBtn>
        ))}
      </div>

      {tab === "proformas" && (
        <div className="space-y-4" data-testid="section-proformas">
          <SectionHeader title="PROFORMAS"
            desc="Toda venta inicia con una proforma. Al AUTORIZAR Y FACTURAR se emite una sola factura con la autorización fiscal vigente y la proforma queda FACTURADA."
            action={<CreateButton onClick={() => navigate("/ventas/nueva")} testid="new-invoice-btn" label="CREAR PROFORMA"
                                  title="Toda venta inicia con una proforma" />} />
          <div className="bg-card border border-border/60 rounded-lg">
            <div className="p-3 flex gap-3 flex-wrap items-center">
              <div className="inline-flex rounded-md border border-border overflow-hidden" role="group" data-testid="proformas-vistas">
                {Object.entries(VISTAS_PROFORMA).map(([k, [l]]) => (
                  <button key={k} onClick={() => setVista(k)} data-testid={`proformas-vista-${k}`}
                    className={`px-3 py-1.5 text-[11px] font-semibold tracking-wider ${vista === k
                      ? "bg-agro-green/15 text-agro-green" : "text-text-muted hover:text-text-primary"}`}>{l}</button>
                ))}
              </div>
              <SearchBox busqueda={busquedaPf} placeholder="Buscar por cliente, código, RTN o número de proforma…" testid="proformas-buscar" label="Buscar proformas" />
            </div>
            <div className="px-3 py-2 border-t border-border/50 flex flex-wrap gap-x-4 text-[11px] tracking-wider text-text-muted">
              <span>VISTA: <strong className="text-text-primary">{VISTAS_PROFORMA[vista][0]}</strong></span>
              <span>DOCUMENTOS: <strong className="text-text-primary" data-testid="proformas-cantidad">{proformasVista.length}</strong></span>
              <span>TOTAL: <strong className="text-agro-green">{fmtL(proformasVista.reduce((s, p) => s + (p.estado === "anulada" ? 0 : (p.total || 0)), 0))}</strong></span>
              <span>PENDIENTES DE AUTORIZACIÓN: <strong className="text-agro-yellow">{pendientes}</strong></span>
            </div>
          </div>
          <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
            <ProformasTable
              items={proformasVista}
              loading={loading}
              converting={converting}
              onView={(p) => view(p, "proforma")}
              onAutorizar={(p) => setConvertModal(p)}
              onDelete={onDeleteProforma}
              onPrint={onPrintProforma}
              flujo={{ user, esRtc, revisa, puedeFacturar, onEnviar, onRevisar: setRevisando, onEditar: (p) => navigate(`/ventas/proforma/${p.id}/editar`) }}
            />
          </div>
        </div>
      )}
      {tab === "facturas" && <FacturasSection soloConsulta={esRtc} onView={(f) => view(f, "factura")} navigate={navigate} />}
      {tab === "promesas" && <PromesasPago />}
      {tab === "notas-debito" && <DebitNotesSection />}
      {tab === "notas-credito" && <CreditNotesSection />}

      {viewing && (viewingType === "proforma"
        ? <ProformaViewer p={viewing} onClose={() => setViewing(null)} onReload={() => view(viewing, "proforma")} />
        : <InvoiceViewer inv={viewing} onClose={() => setViewing(null)} onReload={() => view(viewing, "factura")} />)}
      {revisando && <RevisionModal p={revisando} sinAprobar onClose={() => setRevisando(null)} onDone={() => { setRevisando(null); load(); }} />}
      {convertModal && (
        <ConvertProformaModal p={convertModal} busy={converting === convertModal.id}
          onClose={() => setConvertModal(null)} onConfirm={(opts) => autorizarYFacturar(convertModal, opts)} />
      )}
      {generada && <FacturaGeneradaModal fac={generada} onClose={() => setGenerada(null)} onVer={() => verEnFacturas(generada)}
        onGuia={() => { const id = generada.id; setGenerada(null); navigate(`/ventas/${id}/guia`); }}
        onCobrar={puedeCobrarRecibo(user, hasModule) && Number(generada.saldo_pendiente || 0) > 0.004
          ? () => { const g = generada; setGenerada(null); navigate(`/cxc?cobrar=${encodeURIComponent(g.id)}&cliente=${encodeURIComponent(g.cliente_id)}`); }
          : null} />}
    </Layout>
  );
}

const TABS = [["proformas", "PROFORMAS"], ["facturas", "FACTURAS"], ["promesas", "PROMESAS DE PAGO"], ["notas-debito", "NOTAS DE DÉBITO"], ["notas-credito", "NOTAS DE CRÉDITO"]];

/** Estado visible de la proforma (el interno 'convertida' se muestra como FACTURADA). */
export function estadoProforma(p) {
  if (p.estado === "convertida") return "facturada";
  if (p.estado === "anulada") return "anulada";
  if (["borrador", "devuelta", "rechazada"].includes(p.estado_aprobacion)) return p.estado_aprobacion;
  return "pendiente";   // emitida, sin facturar: espera AUTORIZAR Y FACTURAR
}
const ESTADO_PROFORMA = {
  pendiente: ["PENDIENTE DE AUTORIZACIÓN", "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30"],
  borrador: ["BORRADOR", "bg-white/5 text-text-secondary border-border"],
  devuelta: ["DEVUELTA PARA CORRECCIÓN", "bg-orange-500/10 text-orange-400 border-orange-500/30"],
  rechazada: ["RECHAZADA", "bg-red-500/10 text-red-400 border-red-500/30"],
  facturada: ["FACTURADA", "bg-agro-green/10 text-agro-green border-agro-green/30"],
  anulada: ["ANULADA", "bg-red-500/10 text-red-400 border-red-500/30"],
};
// Por defecto se muestran las que requieren gestión; las facturadas se consultan con su filtro
const VISTAS_PROFORMA = {
  gestion: ["REQUIEREN GESTIÓN", (e) => ["pendiente", "borrador", "devuelta"].includes(e)],
  pendiente: ["PENDIENTES DE AUTORIZACIÓN", (e) => e === "pendiente"],
  facturada: ["FACTURADAS", (e) => e === "facturada"],
  cerradas: ["RECHAZADAS / ANULADAS", (e) => ["rechazada", "anulada"].includes(e)],
  todas: ["TODAS", () => true],
};

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold tracking-wide border-b-2 -mb-px transition-all ${
        active
          ? "border-agro-green text-agro-green"
          : "border-transparent text-text-muted hover:text-text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function ProformasTable({ items, loading, converting, onView, onAutorizar, onDelete, onPrint, flujo = {} }) {
  const propia = (p) => p.created_by === flujo.user?.email;
  const editable = (p) => propia(p) && ["borrador", "devuelta"].includes(p.estado_aprobacion);
  const autorizable = (p) => flujo.puedeFacturar && estadoProforma(p) === "pendiente" && (p.estado_aprobacion !== "pendiente"
    || (flujo.revisa && (!propia(p) || flujo.user?.effective?.is_admin)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-sidebar">
          <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
            <th className="p-3 pl-5">Número</th>
            <th className="p-3">Cliente</th>
            <th className="p-3">Fecha de emisión</th>
            <th className="p-3">Cód. promo</th>
            <th className="p-3 text-right">Total</th>
            <th className="p-3">Estado</th>
            <th className="p-3 pr-5 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading && items.length === 0 ? (
            <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan="7" className="p-12 text-center text-text-muted" data-testid="proformas-vacio">NO HAY PROFORMAS EN ESTA VISTA</td></tr>
          ) : items.map((p) => {
            const est = estadoProforma(p);
            return (
            <tr key={p.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`proforma-row-${p.numero}`}>
              <td className="p-3 pl-5 font-mono text-xs text-agro-green">{p.numero}</td>
              <td className="p-3 text-sm">
                {p.cliente_nombre}
                <div className="text-[10px] text-text-muted">{p.cliente_rtn || "—"}</div>
              </td>
              <td className="p-3 text-xs text-text-secondary">{fmtDate(p.fecha)}</td>
              <td className="p-3 text-xs">
                {p.codigo_promocional ? (
                  <span className="inline-flex items-center gap-1 bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 rounded px-2 py-0.5">
                    <TagIcon size={10} weight="bold" /> {p.codigo_promocional}
                  </span>
                ) : "—"}
              </td>
              <td className="p-3 text-right text-sm font-medium">{fmtL(p.total)}</td>
              <td className="p-3">
                <span data-testid={`estado-${p.numero}`} className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${ESTADO_PROFORMA[est][1]}`}>{ESTADO_PROFORMA[est][0]}</span>
                {est === "facturada" && (
                  <div className="text-[10px] text-text-muted mt-0.5" data-testid={`factura-de-${p.numero}`}>
                    → {p.convertida_factura_numero}{p.autorizada_por ? ` · ${p.autorizada_por_nombre || p.autorizada_por}` : ""}
                    {p.autorizada_at ? ` · ${fmtDateTime(p.autorizada_at)}` : ""}
                  </div>
                )}
                {p.observaciones_revision && ["devuelta", "rechazada"].includes(est) && (
                  <div className="text-[10px] text-text-muted mt-0.5 max-w-[220px]">“{p.observaciones_revision}”</div>
                )}
              </td>
              <td className="p-3 pr-5 text-right">
                <div className="inline-flex gap-1 items-center">
                  <button onClick={() => onView(p)} data-testid={`view-btn-${p.numero}`} className="p-1.5 text-text-muted hover:text-agro-green" title="VER"><EyeIcon size={16} /></button>
                  <button onClick={() => onPrint(p)} data-testid={`print-btn-${p.numero}`} className="p-1.5 text-text-muted hover:text-agro-green" title="IMPRIMIR / PDF"><PrinterIcon size={16} /></button>
                  {editable(p) && (
                    <>
                      <button onClick={() => flujo.onEditar(p)} data-testid={`edit-btn-${p.numero}`}
                        className="px-2.5 py-1 text-[10px] uppercase tracking-wider border border-border rounded hover:border-agro-green/40">EDITAR</button>
                      <button onClick={() => flujo.onEnviar(p)} data-testid={`send-btn-${p.numero}`}
                        className="px-2.5 py-1 text-[10px] uppercase tracking-wider bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 rounded">ENVIAR A AUTORIZACIÓN</button>
                    </>
                  )}
                  {autorizable(p) && (
                    <button
                      onClick={() => onAutorizar(p)}
                      disabled={Boolean(converting)}
                      data-testid={`authorize-btn-${p.numero}`}
                      className="px-2.5 py-1 text-[10px] uppercase tracking-wider whitespace-nowrap bg-agro-green/10 text-agro-green border border-agro-green/30 rounded hover:bg-agro-green/20 flex items-center gap-1 disabled:opacity-50"
                      title="Autoriza la proforma y emite su factura"
                    >
                      <CheckCircleIcon size={12} weight="bold" />
                      {converting === p.id ? "…" : "AUTORIZAR Y FACTURAR"}
                    </button>
                  )}
                  {flujo.revisa && p.estado_aprobacion === "pendiente" && est === "pendiente" && (
                    <button onClick={() => flujo.onRevisar(p)} data-testid={`review-btn-${p.numero}`}
                      className="px-2.5 py-1 text-[10px] uppercase tracking-wider border border-orange-500/30 text-orange-400 rounded">DEVOLVER / RECHAZAR</button>
                  )}
                  {!["facturada", "anulada"].includes(est) && (!flujo.esRtc || editable(p)) && (
                    <button
                      onClick={() => onDelete(p)}
                      data-testid={`delete-btn-${p.numero}`}
                      className="p-1.5 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded"
                      title="ELIMINAR PROFORMA"
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// «Recibir pago»: mismo permiso que el cobro directo en Pagos y cobranza (nunca un RTC: su cobro va a autorización)
function puedeCobrarRecibo(user, hasModule) {
  return !user?.effective?.alcance_regional && (["admin", "accountant", "cxc"].includes(user?.role)
    || (hasModule ? hasModule("cobranza", "crear") : false));
}

const ESTADO_COBRO = {
  pendiente: ["PENDIENTE", "bg-blue-500/10 text-blue-400 border-blue-500/30"],
  parcial: ["PARCIAL", "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30"],
  vencida: ["VENCIDA", "bg-orange-500/10 text-orange-400 border-orange-500/30"],
  pagada: ["PAGADA", "bg-agro-green/10 text-agro-green border-agro-green/30"],
  anulada: ["ANULADA", "bg-red-500/10 text-red-400 border-red-500/30"],
  sin_recibo: ["PENDIENTE · SIN RECIBO", "bg-orange-500/10 text-orange-400 border-orange-500/30"],
};

// Contado emitido antes del cobro con recibo: el sistema lo marcó PAGADA sin recibir el dinero
const sinRecibo = (f) => f.forma_pago === "contado" && !f.cobro_con_recibo && f.estado === "pagada" && !f.es_historica;

function FacturasSection({ soloConsulta, onView, navigate }) {
  const { user, hasModule } = useAuth();
  const esAdmin = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const puedeCobrar = puedeCobrarRecibo(user, hasModule);
  const [regularizar, setRegularizar] = useState(null);   // factura de contado antigua a pasar a cobro con recibo

  const [estadoCobro, setEstadoCobro] = usePageState("estado-cobro-facturas", "");
  const pq = usePeriodQuery("facturas", { estado_cobro: estadoCobro });
  const [shipModal, setShipModal] = useState(null); // {invoice, destinations}
  const onVoid = async (inv) => {
    if (!window.confirm(`¿Anular factura ${inv.numero}? Se revertirá inventario.`)) return;
    try { await api.post(`/invoices/${inv.id}/void`); toast.success("Factura anulada"); pq.reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-4" data-testid="section-facturas">
      <SectionHeader title="FACTURAS"
        desc="Facturas emitidas al AUTORIZAR Y FACTURAR una proforma y facturas cargadas de los estados de cuenta, ubicadas por su FECHA DE EMISIÓN. Una factura con SALDO PENDIENTE se cobra en PAGOS Y COBRANZA: no requiere autorización." />
      <PeriodToolbar pq={pq} testid="fac-period">
        <select value={estadoCobro} onChange={(e) => setEstadoCobro(e.target.value)} data-testid="filter-estado-cobro"
          title="ESTADO DE COBRO" className="bg-app border border-border rounded-md px-3 py-2 text-sm">
          <option value="">ESTADO DE COBRO: TODOS</option>
          <option value="pendiente">CON SALDO PENDIENTE</option>
          <option value="parcial">PARCIAL</option>
          <option value="vencida">VENCIDA</option>
          <option value="pagada">PAGADA</option>
          <option value="anulada">ANULADA</option>
        </select>
      </PeriodToolbar>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <InvoicesTable items={pq.items} loading={pq.loading} onView={onView} onVoid={onVoid}
          onRevert={esAdmin ? (f) => navigate(`/ventas/factura/${f.id}/corregir`) : null}
          onCobrar={puedeCobrar ? (f) => navigate(`/cxc?cobrar=${encodeURIComponent(f.id)}&cliente=${encodeURIComponent(f.cliente_id)}`) : null}
          onRegularizar={esAdmin && puedeCobrar ? setRegularizar : null}
          onOpenShip={(f, dests) => setShipModal({ invoice: f, destinations: dests })} navigate={navigate} soloConsulta={soloConsulta} vacio={pq.vacio} />
        <Pager pq={pq} testid="fac-period" />
      </div>
      {regularizar && (
        <RegularizarContadoModal f={regularizar} onClose={() => setRegularizar(null)}
          onDone={(f) => { setRegularizar(null); navigate(`/cxc?cobrar=${encodeURIComponent(f.id)}&cliente=${encodeURIComponent(f.cliente_id)}`); }} />
      )}
      {shipModal && (
        <ShippingDestinationPickerModal
          data={shipModal}
          onClose={() => setShipModal(null)}
          onConfirm={(destId) => {
            const q = destId ? `?destino_id=${encodeURIComponent(destId)}` : "";
            setShipModal(null);
            navigate(`/ventas/${shipModal.invoice.id}/guia${q}`);
          }}
        />
      )}
    </div>
  );
}

// RTC: la descarga pide la nota y queda en la bitácora de la factura
async function descargarComoRtc(f) {
  const pase = await pedirPaseFactura(f);
  if (!pase) return;
  try {
    await descargarArchivo(conPase(withTemplate(`/invoices/${f.id}/pdf`, "factura"), pase), `factura-${f.numero}.pdf`);
    toast.success("Factura descargada · registrada en la bitácora");
  } catch (e) { toast.error(apiErr(e)); }
}

async function abrirPdfFactura(f, ventana = null) {
  if (esRtcDescarga()) { await descargarComoRtc(f); return; }
  const w = ventana || window.open("about:blank", "_blank");
  try {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const token = getToken();
    const res = await fetch(withTemplate(`${apiBase}/invoices/${f.id}/pdf`, "factura"), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const url = URL.createObjectURL(await res.blob());
    if (w) w.location.href = url; else window.open(url, "_blank");
  } catch (e) { if (w) w.close(); toast.error(`Error abriendo PDF: ${e.message}`); }
}

function InvoicesTable({ items, loading, onView, onVoid, onRevert, onCobrar, onRegularizar, onOpenShip, navigate, soloConsulta = false, vacio = "" }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-sidebar">
          <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
            <th className="p-3 pl-5">Número</th><th className="p-3">Cliente</th>
            <th className="p-3">Fecha de emisión</th><th className="p-3">Vencimiento</th><th className="p-3 text-right">Total</th>
            <th className="p-3 text-right">Saldo pendiente</th><th className="p-3">Estado de cobro</th><th className="p-3 pr-5 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading && items.length === 0 ? <tr><td colSpan="8" className="p-8 text-center text-text-muted">Cargando…</td></tr>
          : items.length === 0 ? <tr><td colSpan="8" className="p-12 text-center text-text-muted" data-testid="facturas-vacio">{vacio}</td></tr>
          : items.map((f) => {
            const ec = sinRecibo(f) ? ESTADO_COBRO.sin_recibo : (ESTADO_COBRO[f.estado_cobro] || ESTADO_COBRO.pendiente);
            const cargada = f.origen === "carga_estado_cuenta" || f.origen === "historial";
            return (
            <tr key={f.id} className={`border-t border-border/40 hover:bg-white/[0.02] ${f.estado === "anulada" ? "opacity-50" : ""}`} data-testid={`invoice-row-${f.numero}`}>
              <td className="p-3 pl-5 font-mono text-xs text-agro-green">{f.numero}
                {cargada && <div className="text-[9px] font-sans tracking-wider text-text-muted" data-testid={`origen-${f.numero}`}
                  title="Documento histórico ya emitido: no requiere autorización">{f.origen === "historial" ? "HISTORIAL" : "CARGA HISTÓRICA"}</div>}
              </td>
              <td className="p-3 text-sm">{f.cliente_codigo && <span className="font-mono text-[11px] text-agro-green mr-1.5" data-testid={`fac-codigo-${f.numero}`}>{f.cliente_codigo}</span>}{f.cliente_nombre}<div className="text-[10px] text-text-muted">{f.cliente_rtn || "—"}{f.proyecto_codigo ? ` · SUBCUENTA ${f.proyecto_codigo}` : ""}</div></td>
              <td className="p-3 text-xs text-text-secondary">{fmtEmision(f.fecha_emision)}</td>
              <td className="p-3 text-xs text-text-secondary">{f.fecha_vencimiento ? fmtEmision(String(f.fecha_vencimiento).slice(0, 10)) : "—"}</td>
              <td className="p-3 text-right text-sm font-medium">{fmtL(f.total)}</td>
              <td className="p-3 text-right text-sm text-agro-yellow">{fmtL(f.saldo_pendiente || 0)}</td>
              <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded border ${ec[1]}`} data-testid={`cobro-${f.numero}`}>{ec[0]}</span></td>
              <td className="p-3 pr-5 text-right whitespace-nowrap">
                <button onClick={() => onView(f)} className="p-1.5 text-text-muted hover:text-agro-green" title="VER" data-testid={`view-invoice-${f.numero}`}><EyeIcon size={16} /></button>
                <button onClick={() => abrirPdfFactura(f)} className="p-1.5 text-text-muted hover:text-agro-green" title="IMPRIMIR / PDF" data-testid={`print-invoice-${f.numero}`}><PrinterIcon size={16} /></button>
                {/* Recibir pago: abre el recibo oficial de Pagos y cobranza con el cliente y esta factura seleccionados.
                    Contado emitido antes del cobro con recibo (marcado PAGADA sin recibo): primero vuelve a quedar con su saldo. */}
                {!soloConsulta && onCobrar && f.estado !== "anulada" && Number(f.saldo_pendiente || 0) > 0.004 && (
                  <button onClick={() => onCobrar(f)} className="p-1.5 text-text-muted hover:text-agro-green" title="RECIBIR PAGO (recibo oficial)"
                    data-testid={`pay-invoice-${f.numero}`}><HandCoinsIcon size={16} /></button>
                )}
                {!soloConsulta && onRegularizar && sinRecibo(f) && !cargada && (
                  <button onClick={() => onRegularizar(f)} className="p-1.5 text-agro-yellow hover:text-agro-green"
                    title="RECIBIR PAGO · esta factura de contado se marcó pagada sin recibo" data-testid={`pay-invoice-${f.numero}`}>
                    <HandCoinsIcon size={16} /></button>
                )}
                {/* Guía de remisión: facturas no anuladas; el destino se elige al generarla */}
                {!soloConsulta && f.estado !== "anulada" && (
                  <button onClick={async () => {
                    try {
                      const { data } = await api.get(`/clients/${f.cliente_id}/shipping-destinations`);
                      onOpenShip(f, data || []);
                    } catch (e) {
                      navigate(`/ventas/${f.id}/guia`);
                    }
                  }}
                          className={`p-1.5 hover:text-blue-400 ${f.imprimir_guia ? "text-blue-400" : "text-text-muted"}`}
                          title="GUÍA DE REMISIÓN"
                          data-testid={`guide-invoice-${f.numero}`}>
                    <TruckIcon size={16} weight={f.imprimir_guia ? "fill" : "regular"} />
                  </button>
                )}
                {!soloConsulta && onRevert && f.estado !== "anulada" && !cargada && (
                  <button onClick={() => onRevert(f)} className="p-1.5 text-text-muted hover:text-agro-yellow" title="CORREGIR FACTURA (conserva el número)"
                    data-testid={`correct-invoice-${f.numero}`}><PencilSimpleIcon size={16} /></button>
                )}
                {!soloConsulta && f.estado !== "anulada" && !cargada && <button onClick={() => onVoid(f)} className="p-1.5 text-text-muted hover:text-red-400" title="ANULAR" data-testid={`void-invoice-${f.numero}`}><ProhibitIcon size={16} /></button>}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FacturaGeneradaModal({ fac, onClose, onVer, onGuia, onCobrar }) {
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-agro-green/40 rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="factura-generada">
        <div className="p-5 border-b border-border flex items-center gap-3">
          <CheckCircleIcon size={28} weight="fill" className="text-agro-green" />
          <div>
            <h3 className="font-heading text-lg">{fac.ya_facturada ? "PROFORMA YA FACTURADA" : "FACTURA GENERADA"}</h3>
            <div className="font-mono text-sm text-agro-green" data-testid="factura-generada-numero">{fac.numero}</div>
          </div>
          <BotonCerrar onClick={vm.cerrar} className="ml-auto" />
        </div>
        <div className="p-5 text-sm space-y-1 text-text-secondary">
          <div>{fac.cliente_nombre}</div>
          {fac.total != null && <div>TOTAL: <strong className="text-text-primary">{fmtL(fac.total)}</strong></div>}
          {Number(fac.saldo_pendiente || 0) > 0.004 && (
            <div className="text-xs text-agro-yellow" data-testid="factura-generada-saldo">
              SALDO PENDIENTE: {fmtL(fac.saldo_pendiente)}{fac.forma_pago === "contado" ? " · CONTADO: el pago se registra con recibo oficial" : ""}
            </div>
          )}
          {fac.autorizacion?.autorizada_por && <div className="text-xs">AUTORIZÓ: {fac.autorizacion.autorizada_por_nombre || fac.autorizacion.autorizada_por} · {fmtDateTime(fac.autorizacion.autorizada_at)}</div>}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2 flex-wrap bg-app/40">
          <button onClick={onVer} data-testid="factura-generada-ver" className="px-3 py-2 text-xs border border-border rounded-md text-text-secondary hover:bg-white/5">VER EN FACTURAS</button>
          {onCobrar && <button onClick={onCobrar} data-testid="factura-generada-cobrar"
            className="px-3 py-2 text-xs border border-agro-green/50 text-agro-green rounded-md flex items-center gap-1.5"><HandCoinsIcon size={14} /> RECIBIR PAGO</button>}
          {fac.imprimir_guia && <button onClick={onGuia} className="px-3 py-2 text-xs border border-blue-500/40 text-blue-400 rounded-md flex items-center gap-1.5"><TruckIcon size={14} /> GUÍA DE REMISIÓN</button>}
          <button onClick={() => abrirPdfFactura(fac)} data-testid="factura-generada-imprimir"
            className="px-4 py-2 text-xs bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md flex items-center gap-1.5">
            <PrinterIcon size={14} weight="bold" /> IMPRIMIR FACTURA
          </button>
        </div>
      </div>
    </div>
  );
}

function ConvertProformaModal({ p, busy, onClose, onConfirm }) {
  const { user } = useAuth();
  // Nota impresa en el PDF de la factura: la agregan el Administrador y el Delegado
  const puedeNota = Boolean(user?.effective?.is_admin || (user?.effective?.roles || []).includes("delegado"));
  const [conNota, setConNota] = useState(false);
  const [nota, setNota] = useState("");
  const [cliente, setCliente] = useState(null);
  const [destinos, setDestinos] = useState([]);
  const [guia, setGuia] = useState(p.imprimir_guia ? "si" : "no");
  const [destinoId, setDestinoId] = useState(p.destino_envio_id || "");
  const [correo, setCorreo] = useState(null); // "si" | "no"
  useEffect(() => {
    api.get(`/clients/${p.cliente_id}`).then(({ data }) => {
      setCliente(data);
      const def = p.enviar_correo !== undefined ? p.enviar_correo : data?.notificar_correo !== false;
      setCorreo(def && data?.email ? "si" : "no");
    }).catch(() => setCorreo("no"));
    api.get(`/clients/${p.cliente_id}/shipping-destinations`).then(({ data }) => setDestinos(data || [])).catch(() => {});
  }, [p.cliente_id, p.enviar_correo]);
  const submit = () => {
    const opts = { imprimir_guia: guia === "si", enviar_correo: correo === "si" };
    if (guia === "si" && destinoId) opts.destino_envio_id = destinoId;
    if (puedeNota && conNota && nota.trim()) opts.nota_factura = nota.trim();
    onConfirm(opts);
  };
  const Opt = ({ val, cur, set, children, testid }) => (
    <button type="button" onClick={() => set(val)} data-testid={testid}
      className={`px-4 py-1.5 text-xs rounded-md border ${cur === val ? "border-agro-green bg-agro-green/10 text-agro-green" : "border-border text-text-secondary"}`}>{children}</button>
  );
  const cvm = useModalForm({ value: { guia, destinoId, correo, nota }, onClose: onClose, onSave: submit, mensaje: "¿Está seguro de que desea autorizar y facturar esta proforma?" });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...cvm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="convert-modal">
        <div className="p-5 border-b border-border">
          <h3 className="font-heading text-lg">AUTORIZAR Y FACTURAR PROFORMA {p.numero}</h3>
          <p className="text-xs text-text-muted mt-1">{p.cliente_nombre} · {fmtL(p.total)}. Se autoriza la proforma y se emite UNA factura: correlativo CAI, descargo de inventario (PEPS) y asiento contable. La proforma queda FACTURADA.</p>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Imprimir guía de remisión</div>
            <div className="flex gap-2">
              <Opt val="si" cur={guia} set={setGuia} testid="convert-guia-si">Sí</Opt>
              <Opt val="no" cur={guia} set={setGuia} testid="convert-guia-no">No</Opt>
            </div>
            {guia === "si" && (
              <select value={destinoId} onChange={(e) => setDestinoId(e.target.value)} data-testid="convert-destino"
                className="mt-2 w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="">{p.destino_envio_snapshot?.alias ? `Destino de la proforma: ${p.destino_envio_snapshot.alias}` : "Dirección principal del cliente"}</option>
                {destinos.map((d) => <option key={d.id} value={d.id}>{d.alias} · {d.direccion}</option>)}
              </select>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Enviar factura por correo al cliente</div>
            <div className="flex gap-2 items-center">
              <Opt val="si" cur={correo} set={setCorreo} testid="convert-correo-si">Sí</Opt>
              <Opt val="no" cur={correo} set={setCorreo} testid="convert-correo-no">No</Opt>
              <span className="text-[11px] text-text-muted">
                {cliente ? (cliente.email ? `${cliente.email} · preferencia: ${cliente.notificar_correo === false ? "no notificar" : "notificar"}` : "El cliente no tiene correo") : "…"}
              </span>
            </div>
          </div>
          {puedeNota && (!conNota ? (
            <button type="button" onClick={() => setConNota(true)} data-testid="convert-agregar-nota"
              className="min-h-[40px] px-3 border border-dashed border-agro-green/60 text-agro-green rounded-md text-xs font-semibold flex items-center gap-1.5 hover:bg-agro-green/10">
              <PlusIcon size={14} weight="bold" /> Agregar nota</button>
          ) : (
            <div data-testid="convert-nota">
              <label htmlFor="convert-nota-txt" className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5 block">Nota de la factura (se imprime en el PDF)</label>
              <textarea id="convert-nota-txt" value={nota} onChange={(e) => setNota(e.target.value.slice(0, 500))} rows={3} autoFocus data-testid="convert-nota-txt"
                placeholder="Ej.: Mercadería entregada en finca. Favor confirmar recepción con el encargado."
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
              <div className="flex justify-between text-[11px] text-text-muted mt-1">
                <span>Aparece en un recuadro «NOTA ADICIONAL» de la factura. Las notas internas no se imprimen.</span>
                <span className="shrink-0 ml-2">{nota.length}/500</span>
              </div>
              <button type="button" onClick={() => { setConNota(false); setNota(""); }} className="mt-1 text-[11px] text-text-muted hover:text-red-300 underline" data-testid="convert-quitar-nota">Quitar nota</button>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2 bg-app/40">
          <button onClick={cvm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:bg-white/5">Cancelar</button>
          <button onClick={cvm.guardar} disabled={(busy || correo === null) || cvm.saving} data-testid="convert-confirm"
            className="px-4 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-50">
            {busy ? "FACTURANDO…" : "AUTORIZAR Y FACTURAR"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceViewer({ inv, onClose, onReload }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const { gestor: gestorNc, admin: esAdminNc } = useGestor();
  const [cambioProy, setCambioProy] = useState(false);
  const [verBitacora, setVerBitacora] = useState(false);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const token = getToken();
  const rtc = esRtcDescarga();              // RTC: solo consulta y descarga con nota

  const openPdf = async () => {
    if (rtc && inv.tipo === "factura") { await descargarComoRtc(inv); if (verBitacora) { setVerBitacora(false); setTimeout(() => setVerBitacora(true), 0); } return; }
    try {
      const url = withTemplate(`${apiBase}/invoices/${inv.id}/pdf`, inv.tipo === "proforma" ? "proforma" : "factura");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`HTTP ${res.status}: ${err.slice(0, 120)}`);
      }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error(`Error abriendo PDF: ${e.message}`);
    }
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invoice-viewer">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-green">Factura {inv.tipo}</div>
            <h3 className="font-heading text-xl font-semibold">{inv.numero}</h3>
          </div>
          <div className="flex items-center gap-2">
            {inv.tipo === "factura" && (
              <button type="button" onClick={() => setVerBitacora((v) => !v)} aria-pressed={verBitacora} data-testid="invoice-bitacora"
                className={`p-2 rounded-md border text-xs ${verBitacora ? "border-agro-green/60 text-agro-green" : "border-border text-text-muted hover:text-text-primary"}`}
                title="Bitácora de actividad de la factura" aria-label="Bitácora de actividad de la factura"><ClockCounterClockwiseIcon size={16} /></button>
            )}
            <button onClick={openPdf} className="px-3 py-2 border border-border rounded-md text-xs flex items-center gap-1.5 hover:bg-white/5" title={rtc ? "Descargar PDF" : "Ver PDF"} data-testid="invoice-pdf-view">
              <FilePdfIcon size={14} /> {rtc ? "Descargar PDF" : "Ver PDF"}
            </button>
            {!rtc && (
              <button onClick={() => setEmailOpen(true)} className="px-3 py-2 bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md text-xs flex items-center gap-1.5" data-testid="invoice-send-email">
                <PaperPlaneTiltIcon size={14} weight="bold" /> Enviar por email
              </button>
            )}
            <button aria-label="Cerrar" onClick={vm.cerrar} className="p-2 text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
          </div>
        </div>
        <div className="p-6">
          {verBitacora && <BitacoraFactura facturaId={inv.id} />}
          {rtc && <p className="mb-4 text-[11px] text-text-muted" data-testid="invoice-solo-lectura">Consulta de solo lectura: puedes ver la factura y descargarla en PDF (cada descarga queda en su bitácora).</p>}
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div>
              <div className="text-[10px] uppercase text-text-muted">Emisor</div>
              <div className="font-medium">AGROPORT S. DE R.L.</div>
              <div className="text-xs text-text-secondary">RTN: 08019995123456</div>
              <div className="text-xs text-text-secondary">Boulevard Suyapa, Tegucigalpa</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-text-muted">CAI</div>
              <div className="font-mono text-xs text-agro-green break-all">{inv.cai}</div>
              <div className="text-xs text-text-secondary mt-2">Fecha: {fmtDate(inv.fecha)}</div>
              <div className="text-xs text-text-secondary">Forma pago: {inv.forma_pago}</div>
            </div>
          </div>
          <div className="bg-app border border-border rounded-md p-3 mb-4">
            <div className="text-[10px] uppercase text-text-muted">Cliente</div>
            <div className="font-medium">{inv.cliente_codigo && <span className="font-mono text-xs text-agro-green mr-2" data-testid="viewer-cliente-codigo">{inv.cliente_codigo}</span>}{inv.cliente_nombre}</div>
            <div className="text-xs text-text-secondary">RTN: {inv.cliente_rtn || "—"} · Vendedor: {inv.vendedor}</div>
            {inv.tipo === "factura" && (
              <div className="mt-2 pt-2 border-t border-border/60 flex flex-wrap items-center justify-between gap-2" data-testid="viewer-proyecto">
                <div className="text-xs"><span className="text-[10px] uppercase text-text-muted block">Proyecto o subcuenta</span>
                  <b>{inv.proyecto_codigo ? `${inv.proyecto_codigo}${inv.proyecto_nombre ? ` · ${inv.proyecto_nombre}` : ""}` : "Cuenta principal del cliente"}</b>
                  {Number(inv.saldo_nc || 0) > 0 && <span className="text-text-muted"> · {fmtL(inv.saldo_nc)} en subcuenta NC</span>}
                  {inv.bloqueo_saldo && <span className="text-agro-yellow block">Solicitud en trámite</span>}</div>
                {gestorNc && !inv.bloqueo_saldo && inv.estado !== "anulada" && (
                  <button type="button" onClick={() => setCambioProy(true)} data-testid="viewer-cambiar-proyecto"
                    className="min-h-[40px] px-3 border border-border rounded-md text-xs font-semibold">{esAdminNc ? "Cambiar" : "Solicitar cambio"}</button>
                )}
              </div>
            )}
          </div>
          {inv.nota_factura && (
            <div className="mb-4 rounded-md border border-agro-green/40 bg-agro-green/5 border-l-4 border-l-agro-yellow px-3 py-2" data-testid="viewer-nota-factura">
              <div className="text-[10px] uppercase tracking-wider text-agro-green font-semibold">Nota adicional (impresa en la factura)</div>
              <div className="text-sm whitespace-pre-wrap">{inv.nota_factura}</div>
              {inv.nota_factura_por && <div className="text-[10px] text-text-muted mt-0.5">Agregada por {inv.nota_factura_por}</div>}
            </div>
          )}
          {inv.credito_override && (
            <div className="mb-4 px-3 py-2 rounded-md border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs" data-testid="credit-override-info">
              <strong>Crédito excedido con autorización</strong> ({inv.credito_override.tipo === "autorizacion_temporal" ? "autorización temporal" : "permiso de override"}) ·
              {" "}autorizó {inv.credito_override.autorizado_por || "—"} · usó {inv.credito_override.usado_por} · {fmtDate(inv.credito_override.fecha)}
              <div className="opacity-80">{inv.credito_override.razon}</div>
            </div>
          )}
          <table className="w-full mb-4">
            <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
              <th className="p-2">Código</th><th className="p-2">Descripción</th><th className="p-2 text-right">Cant.</th>
              <th className="p-2 text-right">Precio</th><th className="p-2">Nivel</th><th className="p-2 text-right">Subtotal</th><th className="p-2 text-right">ISV</th><th className="p-2 text-right">Total</th>
            </tr></thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={i} className="border-b border-border/30">
                  <td className="p-2 font-mono text-xs">{it.codigo}</td>
                  <td className="p-2 text-sm">
                    {descripcionComercial(it.descripcion)}
                    {/* LOTE y BATCH de esta línea (consumos PEPS), como en la factura impresa */}
                    {(inv.lotes_lineas?.[i] || []).map((l, k) => (
                      <div key={k} className="text-[10px] text-text-muted font-mono" data-testid={`lote-linea-${i}-${k}`}>{l}</div>
                    ))}
                  </td>
                  <td className="p-2 text-right text-sm">{it.cantidad}</td>
                  <td className="p-2 text-right text-sm">{fmtL(it.precio_unitario)}</td>
                  <td className="p-2 text-[10px] uppercase">{it.nivel_aplicado || "—"}{it.precio_override && <span className="ml-1 text-agro-yellow" title="Precio modificado">*</span>}</td>
                  <td className="p-2 text-right text-sm">{fmtL(it.subtotal ?? (it.cantidad * it.precio_unitario - (it.descuento || 0)))}</td>
                  <td className="p-2 text-right text-xs">{it.isv_pct}%</td>
                  <td className="p-2 text-right text-sm font-medium">{fmtL(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end">
            <div className="w-full max-w-sm space-y-1 text-sm">
              <Row l="Subtotal" v={fmtL((inv.subtotal_gravado_15 || 0) + (inv.subtotal_gravado_18 || 0) + (inv.subtotal_exento || 0) + (inv.subtotal_exonerado || 0))} />
              <Row l="Gravado 15%" v={fmtL(inv.subtotal_gravado_15)} />
              <Row l="Gravado 18%" v={fmtL(inv.subtotal_gravado_18)} />
              <Row l="Exento" v={fmtL(inv.subtotal_exento)} />
              <Row l="Exonerado" v={fmtL(inv.subtotal_exonerado)} />
              <Row l="ISV 15%" v={fmtL(inv.isv_15)} />
              <Row l="ISV 18%" v={fmtL(inv.isv_18)} />
              <Row l="Descuento" v={fmtL(inv.descuento_total)} />
              <div className="border-t border-border pt-2 mt-2">
                <Row l="TOTAL" v={fmtL(inv.total)} big />
                <Row l="Saldo pendiente" v={fmtL(inv.saldo_pendiente || 0)} yellow />
              </div>
            </div>
          </div>

          <BitacoraTarifas items={inv.bitacora_tarifas} />
          <EmailHistory items={inv.email_history} />

          {/* Centro de documentos: comprobantes, factura firmada, notas, etc. (no para el RTC: solo consulta) */}
          {!rtc && <div className="mt-6">
            <DocumentCenter
              entityType={inv.tipo === "compra" ? "purchase_invoice" : "invoice"}
              entityId={inv.id}
              title="Adjuntos (comprobantes, factura firmada, notas)"
            />
          </div>}
        </div>
      </div>
      {cambioProy && <CambioProyectoModal factura={inv} onClose={() => setCambioProy(false)} onDone={() => { setCambioProy(false); onReload && onReload(); }} />}
      {emailOpen && (
        <SendDocEmailModal title="Enviar factura por email" docLabel="Factura"
          endpoint={`/invoices/${inv.id}/send-email`} clienteId={inv.cliente_id}
          defaultSubject={`Factura ${inv.numero} · Agroport Honduras`}
          onClose={() => setEmailOpen(false)} onSent={() => onReload && onReload()} />
      )}
    </div>
  );
}

function ProformaViewer({ p: pIni, onClose, onReload }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [p, setP] = useState(pIni);
  const [tarifasOpen, setTarifasOpen] = useState(false);
  const { user } = useAuth();
  const editaTarifa = puedeCambiarTarifa(user) && !["convertida", "anulada"].includes(p.estado);
  const openPdf = async () => {
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const token = getToken();
      const res = await fetch(withTemplate(`${apiBase}/invoices/${p.id}/pdf`, "proforma"), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { toast.error(`Error abriendo PDF: ${e.message}`); }
  };
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="proforma-viewer">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Proforma</div>
            <h3 className="font-heading text-xl font-semibold">{p.numero}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openPdf} className="px-3 py-2 border border-border rounded-md text-xs flex items-center gap-1.5 hover:bg-white/5" data-testid="proforma-pdf-view">
              <FilePdfIcon size={14} /> Ver PDF
            </button>
            {editaTarifa && (
              <button onClick={() => setTarifasOpen(true)} data-testid="proforma-editar-tarifa"
                className="px-3 py-2 border border-agro-yellow/40 text-agro-yellow rounded-md text-xs hover:bg-agro-yellow/10">Tarifa / P0</button>
            )}
            <button onClick={() => setEmailOpen(true)} className="px-3 py-2 bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md text-xs flex items-center gap-1.5" data-testid="proforma-send-email">
              <PaperPlaneTiltIcon size={14} weight="bold" /> Enviar por email
            </button>
            <button aria-label="Cerrar" onClick={vm.cerrar} className="p-2 text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
          </div>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase text-text-muted">Cliente</div>
              <div className="font-medium">{p.cliente_nombre}</div>
              <div className="text-xs text-text-secondary">RTN: {p.cliente_rtn || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-text-muted">Vendedor / Región</div>
              <div className="text-sm">{p.vendedor || "—"} {p.region && `· ${p.region}`}</div>
              <div className="text-xs text-text-secondary">Tipo: {p.tipo_venta}</div>
            </div>
          </div>
          {p.codigo_promocional && (
            <div className="bg-agro-yellow/10 border border-agro-yellow/30 rounded-md p-3 flex items-center gap-2">
              <TagIcon size={14} className="text-agro-yellow" />
              <span className="text-xs">Código promocional: <strong className="text-agro-yellow font-mono">{p.codigo_promocional}</strong></span>
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
                <th className="p-2">Producto</th><th className="p-2 text-right">Cant.</th>
                <th className="p-2">Tarifa</th><th className="p-2 text-right">Precio</th><th className="p-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {p.items?.map((it, i) => (
                <tr key={i} className="border-b border-border/30">
                  <td className="p-2 text-xs">{it.codigo} — {descripcionComercial(it.descripcion)}</td>
                  <td className="p-2 text-right text-sm">{it.cantidad}</td>
                  <td className="p-2 text-xs" data-testid={`proforma-tarifa-${i}`}>
                    <span className={it.tarifa_cambiada ? "text-agro-yellow font-semibold" : "text-text-secondary"}>{(it.nivel_aplicado || "").toUpperCase()}</span>
                    {it.tarifa_cambiada && it.nivel_cliente && <span className="text-[10px] text-text-muted"> (cliente {it.nivel_cliente.toUpperCase()})</span>}
                  </td>
                  <td className="p-2 text-right text-sm">{fmtL(it.precio_unitario)}</td>
                  <td className="p-2 text-right text-sm font-medium">{fmtL(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-border pt-3 flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-wider text-text-muted">Total</span>
            <span className="font-heading text-2xl text-agro-green">{fmtL(p.total)}</span>
          </div>
          {p.notas && <div className="text-xs text-text-secondary"><strong>Notas:</strong> {p.notas}</div>}
          <BitacoraTarifas items={p.bitacora_tarifas} />
          <AprobacionInfo p={p} />
          <EmailHistory items={p.email_history} />
        </div>
      </div>
      {tarifasOpen && (
        <TarifasModal p={p} onClose={() => setTarifasOpen(false)}
          onSaved={(d) => { setTarifasOpen(false); setP(d); onReload && onReload(); }} />
      )}
      {emailOpen && (
        <SendDocEmailModal title="Enviar proforma por email" docLabel="Proforma"
          endpoint={`/invoices/${p.id}/send-email`} clienteId={p.cliente_id}
          defaultSubject={`Proforma ${p.numero} · Agroport Honduras`}
          onClose={() => setEmailOpen(false)} onSent={() => onReload && onReload()} />
      )}
    </div>
  );
}

function Row({ l, v, big, yellow }) {
  return (
    <div className="flex justify-between">
      <span className={`${big ? "font-heading text-base" : "text-xs"} text-text-secondary`}>{l}</span>
      <span className={`${big ? "font-heading text-lg font-semibold text-agro-green" : yellow ? "text-agro-yellow font-medium" : "text-sm"}`}>{v}</span>
    </div>
  );
}

function ShippingDestinationPickerModal({ data, onClose, onConfirm }) {
  const { invoice, destinations } = data;
  const [chosen, setChosen] = useState(invoice.destino_envio_id || "");

  const vm = useModalForm({ value: chosen, onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="ship-picker-modal" {...vm.overlayProps}>
      <div className="bg-card border border-blue-500/40 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 border-b border-border/50 flex items-start justify-between sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center">
              <TruckIcon size={22} weight="fill" className="text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-text-primary">¿A dónde enviar este pedido?</h3>
              <p className="text-xs text-text-muted">
                Factura {invoice.numero} · {invoice.cliente_nombre}
              </p>
            </div>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary" data-testid="ship-picker-close">
            <XIcon size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* Opción: dirección del cliente */}
          <label
            className={`block cursor-pointer p-4 rounded-lg border-2 transition ${
              chosen === "" ? "border-blue-500 bg-blue-500/5" : "border-border/60 hover:border-blue-500/40"
            }`}
            data-testid="ship-opt-default"
          >
            <div className="flex items-start gap-3">
              <input type="radio" name="dest" checked={chosen === ""} onChange={() => setChosen("")}
                className="mt-1 accent-blue-500" />
              <div>
                <div className="text-sm font-semibold text-text-primary">Dirección principal del cliente</div>
                <div className="text-xs text-text-muted">La dirección registrada en el perfil del cliente.</div>
              </div>
            </div>
          </label>

          {destinations.length === 0 ? (
            <div className="bg-agro-yellow/5 border border-agro-yellow/30 rounded-lg p-3 text-xs text-text-secondary">
              Este cliente no tiene destinos de envío alternativos. Puedes agregarlos desde
              el perfil del cliente (ir a Clientes → ver detalle → Referencias de envío).
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-wider text-text-muted mt-4 mb-2">
                Destinos alternativos ({destinations.length})
              </div>
              {destinations.map((d) => (
                <label key={d.id}
                  className={`block cursor-pointer p-4 rounded-lg border-2 transition ${
                    chosen === d.id ? "border-blue-500 bg-blue-500/5" : "border-border/60 hover:border-blue-500/40"
                  }`}
                  data-testid={`ship-opt-${d.id}`}
                >
                  <div className="flex items-start gap-3">
                    <input type="radio" name="dest" checked={chosen === d.id} onChange={() => setChosen(d.id)}
                      className="mt-1 accent-blue-500" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-text-primary">{d.alias}</div>
                      <div className="text-xs text-text-secondary mt-0.5">{d.direccion}</div>
                      {(d.encargado || d.telefono) && (
                        <div className="text-[11px] text-text-muted mt-1">
                          {d.encargado && <span className="inline-flex items-center gap-1"><UserIcon size={12} aria-label="Encargado" /> {d.encargado}</span>}
                          {d.encargado && d.telefono && <span> · </span>}
                          {d.telefono && <span className="inline-flex items-center gap-1"><PhoneIcon size={12} aria-label="Teléfono" /> {d.telefono}</span>}
                        </div>
                      )}
                      {d.referencia && <div className="text-[11px] italic text-text-muted mt-1">Ref: {d.referencia}</div>}
                    </div>
                  </div>
                </label>
              ))}
            </>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-border/40 mt-4">
            <button onClick={vm.cerrar}
              className="px-4 py-2 text-sm border border-border rounded-md hover:bg-white/5">
              Cancelar
            </button>
            <button onClick={() => onConfirm(chosen)}
              data-testid="ship-picker-confirm"
              className="px-5 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-md transition inline-flex items-center gap-2">
              <TruckIcon size={14} weight="bold" /> Ver guía de remisión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/** Factura de contado que el sistema dio por pagada al emitirla (sin recibo): pasa a cobro con recibo oficial. */
function RegularizarContadoModal({ f, onClose, onDone }) {
  const [motivo, setMotivo] = useState("Contado sin recibo: el cobro se registrará con recibo oficial");
  const [diag, setDiag] = useState(null);
  useEffect(() => {
    api.get("/invoices/contado-sin-recibo", { params: { factura_id: f.id } }).then(({ data }) => setDiag((data.facturas || []).find((x) => x.id === f.id) || {}))
      .catch(() => setDiag({}));
  }, [f.id]);
  const guardar = async () => {
    if (motivo.trim().length < 5) { toast.error("Indica el motivo"); return false; }
    try {
      await api.post(`/invoices/${f.id}/regularizar-contado`, { motivo });
      toast.success(`Factura ${f.numero}: saldo pendiente ${fmtL(f.total)}. Registra el pago con el recibo oficial.`);
      onDone(f);
    } catch (e) { toast.error(apiErr(e)); return false; }
  };
  const m = useModalForm({ value: { motivo }, onClose, onSave: guardar, listo: diag !== null,
    mensaje: `¿Está seguro? La factura ${f.numero} vuelve a quedar con saldo pendiente de ${fmtL(f.total)} (asiento de reclasificación Caja → Cuentas por cobrar) y se abre el recibo oficial.` });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-agro-yellow/40 rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="regularizar-modal">
        <div className="p-5 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg">RECIBIR PAGO · {f.numero}</h3>
            <p className="text-xs text-text-muted mt-1">{f.cliente_nombre} · {fmtL(f.total)}</p>
          </div>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-5 space-y-3 text-sm">
          <p className="text-text-secondary text-xs">
            Esta factura de contado se emitió antes del cobro con recibo: el sistema la marcó PAGADA y llevó la venta a Caja
            sin que se recibiera el dinero. Al continuar, vuelve a quedar con su saldo pendiente (en Pagos y cobranza y en la
            cuenta del cliente) y se abre el recibo oficial. Si cierras el recibo sin registrarlo, la factura queda pendiente.
            No se borra ningún registro: se agrega un asiento de reclasificación (Caja → Cuentas por cobrar).
          </p>
          {diag === null ? <div className="text-xs text-text-muted">Revisando la factura…</div> : (
            <ul className="text-xs space-y-1 bg-app border border-border rounded p-3" data-testid="regularizar-diagnostico">
              <li>Asiento de venta llevado a Caja (1101): <b>{fmtL(diag.asiento_caja || 0)}</b> → se reclasifica a CxC</li>
              <li>Ingreso automático a caja (tesorería): <b>{fmtL(diag.ingreso_caja || 0)}</b>{(diag.ingreso_caja || 0) > 0 ? " → se reversa" : ""}</li>
              <li>Recibos registrados: <b>{diag.recibos ?? 0}</b> · Notas de crédito vigentes: <b>{diag.notas_credito ?? 0}</b></li>
            </ul>
          )}
          <label className="block text-[10px] uppercase tracking-wider text-text-muted">Motivo *</label>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="regularizar-motivo"
            className="w-full bg-app border border-border rounded px-3 py-2 text-sm" />
        </div>
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
          <button onClick={m.guardar} disabled={m.saving || diag === null} data-testid="regularizar-confirmar"
            className="px-4 py-2 text-sm bg-agro-green text-black rounded-md disabled:opacity-50">
            {m.saving ? "Procesando…" : "Continuar al recibo"}</button>
        </div>
      </div>
    </div>
  );
}
